import {ChangeDetectionStrategy, Component, computed, effect, ElementRef, Injectable, input, signal, viewChild} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erTomBeslutningsrad, MøteprotokollRedigerBeslutningerKomponent, SkjemaRadType as BeslutningSkjema} from "../møteprotokoll-rediger-beslutninger/møteprotokoll-rediger-beslutninger";
import {AgendaelementModell, AvstemningId, AvstemningMedResultatModell, AvstemningRessursClient, AvstemningsstatusModell, GruppeId, SaksreferatSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {TranslateModule} from "@ngx-translate/core";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {AvstemningListevisningKomponent} from "../../../avstemning/komponenter/avstemning-listevisning/avstemning-listevisning";
import {MatButton} from "@angular/material/button";
import {RammeInnholdKomponent, RammeMedTittelKomponent, RammeTittelKomponent} from "web-ng2/delt/komponenter/ramme-med-tittel/ramme-med-tittel";
import {HtmlFormatertKomponent} from "web-ng2/delt/komponenter/html-formatert/html-formatert";
import {erIkkeTomStreng, erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {MatTooltip} from "@angular/material/tooltip";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MatIcon} from "@angular/material/icon";
import {AgendaelementModellDLM} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {scrollTilSenter} from "web-ng2/delt/dom/scrollIntoView";
import {AnimationBuilder} from "@angular/animations";
import {animerBlink} from "web-ng2/delt/animasjoner";
import {GenerertBeslutning, GenerertOppgave, GenerertSaksreferat} from "felles-ng2/modell/transkripsjonModellHjelpere";

const debug = initDebugger('MøteprotokollRedigerSaksreferatKomponent');

export type SkjemaType = ReturnType<typeof MøteprotokollRedigerSaksreferatKomponent.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];

/**
 * Hva møteopptaket ga for én sak: referatet KI-en genererte, og om det finnes lydopptak av
 * møtebehandlingen i det hele tatt – uten opptak er det ingenting å generere referat fra.
 */
export interface GenerertSaksreferatInformasjon {
    /** Referatet KI-en genererte for saken fra lydopptakene, null når det ikke er generert noe  */
    saksreferat: GenerertSaksreferat | null;
    /** Sann når det er gjort lydopptak av møtebehandlingen */
    lydopptakEksisterer: boolean;
    /** Sann når referat er generert fra lydopptakene, også når genereringen ikke ga noe referat for denne saken */
    saksreferatGenerert: boolean;
}

/**
 * Skjema for redigering av saksreferat
 */
@Component({
    selector: 'moeteprotokoll-rediger-saksreferat',
    templateUrl: 'møteprotokoll-rediger-saksreferat.html',
    styleUrl: 'møteprotokoll-rediger-saksreferat.scss',
    imports: [
        MatFormField,
        MatLabel,
        TranslateModule,
        RiktekstRedigeringKomponent,
        MøteprotokollRedigerBeslutningerKomponent,
        AvstemningListevisningKomponent,
        MatButton,
        RammeMedTittelKomponent,
        HtmlFormatertKomponent,
        MatTooltip,
        MatIcon,
        RammeInnholdKomponent,
        RammeTittelKomponent,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        FormaterPlainDatePipe
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteprotokollRedigerSaksreferatKomponent {

    private referattekstRedigeringKomponent = viewChild.required(RiktekstRedigeringKomponent);
    private redigerBeslutningerKomponent = viewChild.required(MøteprotokollRedigerBeslutningerKomponent);
    private generertProtokollElement = viewChild('generertProtokollElement', {read: ElementRef});

    skjema = input.required<SkjemaType>();
    gruppeId = input.required<GruppeId>();
    avstemningsstatus = input.required<AvstemningsstatusModell>();
    eksponerVisSaksbeskrivelseKnapp = input(true);
    eksponerVisGenerertSaksreferatKnapp = input(true);
    /** Hva møteopptaket ga for denne saken, se {@link GenerertSaksreferatInformasjon} */
    generertSaksreferat = input<GenerertSaksreferatInformasjon>({saksreferat: null, lydopptakEksisterer: false, saksreferatGenerert: false});

    /** Sann dersom det generert møtereferatet inneholdt en oppsummering for denne saken */
    harGenerertSaksreferatInnhold = computed(() => erIkkeNil(this.generertSaksreferat().saksreferat));
    /** Skiller mellom at møtet mangler lydopptak og at opptaket finnes uten at referatet er generert – bruker kan gjøre noe med det siste */
    visGenerertSaksreferatHjelpetekst = computed(() => {
        if (!this.generertSaksreferat().lydopptakEksisterer)
            return this.oversettelser.oversett(i18n('Det er ikke gjort lydopptak av dette møtet'))
        else if (!this.generertSaksreferat().saksreferatGenerert)
            return this.oversettelser.oversett(i18n('Forslag til protokoll er enda ikke generert for dette møtet'))
        return this.oversettelser.oversett(i18n('Klikk for å vise transkribert møtereferat for denne saken'));
    });
    viserGenerertReferat = signal(false);

    harSaksbeskrivelse = computed(() => erIkkeTomStreng(this.skjema().value.sak!.beskrivelse));
    visSaksbeskrivelseHjelpetekst = computed(() => this.harSaksbeskrivelse() ?
      this.oversettelser.oversett(i18n('Klikk for å vise saksbeskrivelse er registrert på denne saken')) :
      this.oversettelser.oversett(i18n('Ingen saksbeskrivelse er registrert på denne saken')));
    viserSaksbeskrivelse = signal(false);

    /** Avstemninger knyttet til saken. Definert som signal da vi ved handlingen 'stengAvstemning' trenger å oppdatere listen og så signalisert til GUI at det har skjedd endringer i denne */
    avstemninger = signal<AvstemningMedResultatModell[]>([]);

    static opprettSkjema() {
        return new FormGroup({
            // @ts-ignore // sak blir satt i populerSkjema
            sak: new FormControl<AgendaelementModellDLM>(undefined, {nonNullable: true, validators: Validators.required}),
            referat: new FormControl('', {nonNullable: true, validators: FeltValidatorer.htmlTekst}),
            beslutninger: MøteprotokollRedigerBeslutningerKomponent.opprettSkjema()
        });
    }

    static populerSkjema(skjema: SkjemaType, sak: AgendaelementModellDLM): SkjemaType {
        skjema.patchValue({sak: sak, referat: sak.referat});
        (sak as AgendaelementModell).beslutninger.forEach(beslutning => //@todo last vi datatjeneste
          skjema.controls.beslutninger.controls.eksisterende.push(MøteprotokollRedigerBeslutningerKomponent.opprettBeslutningSkjema(beslutning)));
        return skjema;
    }

    static somSpesifikasjon(verdier: SkjemaVerdier): SaksreferatSpesifikasjon {
        return {
            sakId: verdier.sak!.id!,
            referat: verdier.referat!,
            beslutninger: MøteprotokollRedigerBeslutningerKomponent.somSpesifikasjoner(verdier.beslutninger!)
        };
    }

    static kopierSaksbeskrivelseTilReferat(skjema: SkjemaType) {
        let skjemaverdier = skjema.value;
        skjema.controls.referat.setValue(skjemaverdier.referat + skjemaverdier.sak!.beskrivelse);
    }

    constructor(private element: ElementRef<HTMLElement>,
                private avhengigheter: MøteprotokollRedigerSaksreferatKomponentAvhengigheter,
                private oversettelser: Oversettelser,
                private animationBuilder: AnimationBuilder) {
        effect(() => this.avstemninger.set((this.skjema().value.sak as AgendaelementModell)!.avstemninger)); //@todo last via datatjeneste
    }

    visSaksbeskrivelse() {
        this.viserSaksbeskrivelse.set(true);
    }

    skjulSaksbeskrivelse() {
        this.viserSaksbeskrivelse.set(false);
    }

    limInnSaksbeskrivelse() {
        MøteprotokollRedigerSaksreferatKomponent.kopierSaksbeskrivelseTilReferat(this.skjema());
        this.skjulSaksbeskrivelse();
    }

    // Vi lagrer bare info om du har trykket på knappen mens du er inne på rediger. Neste gang du går inn er knappen tilgjengelig. Hindrer bare at folk ikke trykker flere ganger.
    private _harTrykketOpprettAvstemning: AvstemningId[] = [];

    harTrykketOpprettAvstemning(avstemningId: AvstemningId) {
        return this._harTrykketOpprettAvstemning.includes(avstemningId);
    }

    visGenerertReferat() {
        this.viserGenerertReferat.set(true);
    }

    skjulGenerertReferat() {
        this.viserGenerertReferat.set(false);
    }

    /** Scroller den genererte protokollen til midten av skjermen og blinker bakgrunnen dens, slik at bruker ser hvor referatet kom */
    async scrollTilGenerertReferat() {
        scrollTilSenter(this.element.nativeElement);
        //vent litt før vi blinker bakgrunn - slik at scrollingen er ferdig
        setTimeout(() => this.blinkGenerertProtokoll(), 800);
    }

    /** Hjelpetekst på knappen som legger en generert oppgave under en beslutning */
    leggGenerertOppgaveUnderHjelpetekst(): string {
        return this.beslutningerOppgaveKanLeggesUnder().length == 0 ?
          this.oversettelser.oversett(i18n('Registrer en beslutning på saken først, oppgaven må ligge under en beslutning')) :
          this.oversettelser.oversett(i18n('Velg beslutningen oppgaven skal ligge under'));
    }

    limInnAltGenerert() {
        this.limInnAltGenerertFra(this.generertSaksreferat().saksreferat!);
    }

    /** Legger den genererte oppsummeringen bak referatteksten, slik {@link limInnSaksbeskrivelse} gjør med saksbeskrivelsen */
    limInnGenerertOppsummering() {
        this.limInnGenerertOppsummeringFra(this.generertSaksreferat().saksreferat);
    }

    /** Registrerer den genererte beslutningen som en ny beslutning på saken, med oppgavene sine */
    registrerGenerertBeslutning(beslutning: GenerertBeslutning) {
        this.redigerBeslutningerKomponent().opprettBeslutningFraMøteopptak(beslutning);
    }

    /** Registrerer den genererte oppgaven under beslutningen bruker valgte */
    registrerGenerertOppgave(oppgave: GenerertOppgave, beslutning: BeslutningSkjema) {
        this.redigerBeslutningerKomponent().opprettOppgaveFraMøteopptak(beslutning, oppgave);
    }

    /**
     * Limer inn hele det genererte saksreferatet: oppsummeringen i referatfeltet og alle beslutningene
     * med oppgavene sine som nye beslutninger.
     *
     * Tar saksreferatet som argument, ikke fra {@link #generertSaksreferat}, slik at den kan kalles
     * rett etter at referatet er generert – før inputen har rukket å bli propagert.
     */
    limInnAltGenerertFra(saksreferat: GenerertSaksreferat) {
        this.limInnGenerertOppsummeringFra(saksreferat);
        saksreferat.beslutninger.forEach(beslutning => this.registrerGenerertBeslutning(beslutning));
    }

    /** Beslutningene på saken en generert oppgave kan legges under: registrerte og påbegynte, uten forslag og slettede */
    beslutningerOppgaveKanLeggesUnder(): BeslutningSkjema[] {
        const beslutninger = this.skjema().controls.beslutninger.controls;
        return [
            ...beslutninger.eksisterende.controls.filter(rad => !rad.value.erForslag && !rad.value.slettet),
            ...beslutninger.nye.controls.filter(rad => !erTomBeslutningsrad(rad.value))
        ];
    }

    registrerAvstemningSomBeslutning(avstemning: AvstemningMedResultatModell, redigerBeslutningerKomponent: MøteprotokollRedigerBeslutningerKomponent) {
        redigerBeslutningerKomponent.opprettBeslutningFraAvstemning(avstemning, this.avstemningsstatus());
        this._harTrykketOpprettAvstemning.push(avstemning.id);
    }

    async stengAvstemning(avstemning: AvstemningMedResultatModell) {
        const oppdatertAvstemning = await this.avhengigheter.stengAvstemning(avstemning.id);
        //oppdater den aktuelle avstemningen i listen over avstemninger
        this.avstemninger.update(avstemninger => {
            const indeks = avstemninger.indexOf(avstemning)!;
            avstemninger[indeks] = oppdatertAvstemning;
            return avstemninger;
        });
    }

    giReferatFeltFokus() {
        this.referattekstRedigeringKomponent().giTekstfeltFokus();
    }

    //private hjelpemetoder

    /** Blinker bakgrunnen på den genererte protokollen to ganger. Gjør ingenting når protokollen ikke vises - da er det ingenting å peke ut */
    private blinkGenerertProtokoll() {
        const element = this.generertProtokollElement()?.nativeElement;
        if (erNil(element)) return;
        //blinker tilbake til panelets egen bakgrunnsfarge framfor animasjonens standard 'initial', som ville blinket mot hvitt
        const bakgrunnsfarge = getComputedStyle(element).backgroundColor || 'initial';
        const blink = this.animationBuilder.build(animerBlink({antallBlink: 2, initialFargefarge: bakgrunnsfarge})).create(element);
        blink.onDone(() => blink.destroy()); //rydder bort stilene animasjonen la på elementet
        blink.play();
    }

    private limInnGenerertOppsummeringFra(saksreferat: GenerertSaksreferat | null) {
        const oppsummering = saksreferat?.oppsummering;
        if (erTomEllerNil(oppsummering)) return;
        this.skjema().controls.referat.setValue(this.skjema().value.referat + oppsummering);
    }
}

@Injectable()
export class MøteprotokollRedigerSaksreferatKomponentAvhengigheter {

    constructor(private avstemningRessurs: AvstemningRessursClient) {
    }

    stengAvstemning(id: AvstemningId): Promise<AvstemningMedResultatModell> {
        return this.avstemningRessurs.stengForAvstemning(id).somPromise()
    }
}
